// src/pages/portal/AgencyPortal.js
import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { auth, db } from "../../firebaseConfig";
import {
  createUserWithEmailAndPassword,
  signInWithEmailAndPassword,
  signOut,
  onAuthStateChanged,
} from "firebase/auth";
import {
  doc,
  setDoc,
  getDoc,
  serverTimestamp,
} from "firebase/firestore";
import toast, { Toaster } from "react-hot-toast";

/* ----------------- Constants ----------------- */

// Gujarat districts (abhi ke liye – baad me aur add kar sakte ho)
const GUJ_DISTRICTS = [
  "Banaskantha",
  "Mahesana",
  "Patan",
  "Gandhinagar",
  "Ahmedabad",
  "Sabarkantha",
  "Aravalli",
  "Kutch",
  "Surat",
  "Vadodara",
  "Bhavnagar",
  "Rajkot",
];

// Office types
const OFFICE_TYPES = [
  { id: "BANK", label: "Bank / Financing Agency" },
  { id: "DISTRICT_REG", label: "District Registrar Office" },
  { id: "STATE_REG", label: "State Registrar Office" },
  { id: "CENTRAL_AGENCY", label: "Central Agency" },
];

// Central agencies – ONLY these 5 as per your requirement
const CENTRAL_AGENCIES = [
  { id: "NAFED", label: "NAFED" },
  { id: "GUJCOMASOL", label: "Gujcomasol" },
  { id: "NCCF", label: "NCCF" },
  { id: "NBCC", label: "NBCC" },
  { id: "NABCONS", label: "NABCONS" },
];

/* ----------------- Small UI atoms ----------------- */

const cx = (...xs) => xs.filter(Boolean).join(" ");

function Label({ children }) {
  return (
    <label className="text-sm font-semibold text-gray-700">
      {children}
    </label>
  );
}
function Input({ type = "text", value, onChange, ...p }) {
  return (
    <input
      type={type}
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="mt-1 w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
      {...p}
    />
  );
}
function Checkbox({ checked, onChange }) {
  return (
    <input
      type="checkbox"
      checked={checked}
      onChange={(e) => onChange(e.target.checked)}
      className="w-4 h-4 text-emerald-600"
    />
  );
}
function Card({ title, children }) {
  return (
    <div className="rounded-2xl border border-emerald-200 bg-white shadow-md">
      {title && (
        <div className="px-5 py-3 border-b border-emerald-50 bg-gradient-to-r from-emerald-600 to-teal-500 text-white rounded-t-2xl">
          <div className="text-sm font-semibold">{title}</div>
        </div>
      )}
      <div className="p-5">
        <div className="grid gap-4">{children}</div>
      </div>
    </div>
  );
}
function Badge({ color = "gray", children }) {
  const map = {
    green: "bg-emerald-100 text-emerald-700",
    yellow: "bg-amber-100 text-amber-700",
    red: "bg-red-100 text-red-700",
    gray: "bg-gray-100 text-gray-700",
  };
  return (
    <span
      className={cx(
        "inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold",
        map[color] || map.gray
      )}
    >
      {children}
    </span>
  );
}

/* ================== MAIN PAGE ================== */

export default function AgencyPortal() {
  const location = useLocation();
  const search = new URLSearchParams(location.search);
  const urlType = search.get("type"); // bank | registrar | agency

  const [tab, setTab] = useState("login"); // login | register
  const [dark, setDark] = useState(false);

  // registration state
  const [fullName, setFullName] = useState("");
  const [officeName, setOfficeName] = useState("");
  const [officeType, setOfficeType] = useState("BANK");
  const [centralAgencyId, setCentralAgencyId] = useState("");
  const [regEmail, setRegEmail] = useState("");
  const [regPhone, setRegPhone] = useState("");
  const [regPassword, setRegPassword] = useState("");
  const [state, setState] = useState("Gujarat");
  const [requestedDistricts, setRequestedDistricts] = useState([]);
  const [regLoading, setRegLoading] = useState(false);

  // login state
  const [logEmail, setLogEmail] = useState("");
  const [logPassword, setLogPassword] = useState("");
  const [logLoading, setLogLoading] = useState(false);

  // logged in profile
  const [profile, setProfile] = useState(null);

  // set default officeType based on ?type=
  useEffect(() => {
    if (urlType === "bank") setOfficeType("BANK");
    else if (urlType === "registrar") setOfficeType("DISTRICT_REG");
    else if (urlType === "agency") setOfficeType("CENTRAL_AGENCY");
  }, [urlType]);

  // auth listener – if already logged-in
  useEffect(() => {
    const unsub = onAuthStateChanged(auth, async (user) => {
      if (!user) {
        setProfile(null);
        return;
      }
      const ref = doc(db, "portal_users", user.uid);
      const snap = await getDoc(ref);
      if (snap.exists()) {
        setProfile(snap.data());
      } else {
        setProfile(null);
      }
    });
    return () => unsub();
  }, []);

  const toggleDistrict = (d) => {
    setRequestedDistricts((old) =>
      old.includes(d) ? old.filter((x) => x !== d) : [...old, d]
    );
  };

  /* ---------- REGISTER HANDLER ---------- */
  const handleRegister = async (e) => {
    e.preventDefault();
    if (
      !fullName ||
      !officeName ||
      !regEmail ||
      !regPhone ||
      !regPassword ||
      !officeType
    ) {
      toast.error("Please fill all required fields");
      return;
    }

    if (
      !requestedDistricts.length &&
      officeType !== "STATE_REG" &&
      officeType !== "CENTRAL_AGENCY"
    ) {
      toast.error("Select atleast one district");
      return;
    }

    if (officeType === "CENTRAL_AGENCY" && !centralAgencyId) {
      toast.error("Select central agency");
      return;
    }

    try {
      setRegLoading(true);
      const email = regEmail.trim().toLowerCase();
      const phone = regPhone.trim();

      const cred = await createUserWithEmailAndPassword(
        auth,
        email,
        regPassword
      );
      const uid = cred.user.uid;

      const requested =
        officeType === "STATE_REG" || officeType === "CENTRAL_AGENCY"
          ? GUJ_DISTRICTS
          : requestedDistricts;

      await setDoc(doc(db, "portal_users", uid), {
        uid,
        fullName: fullName.trim(),
        officeName: officeName.trim(),
        officeType,
        centralAgencyId:
          officeType === "CENTRAL_AGENCY" ? centralAgencyId : null,
        centralAgencyName:
          officeType === "CENTRAL_AGENCY"
            ? CENTRAL_AGENCIES.find((a) => a.id === centralAgencyId)?.label ||
              ""
            : "",
        email,
        phone,
        state,
        requestedDistricts: requested,
        assignedDistricts: [],
        status: "pending", // PMC admin approval pending
        adminRemark: "",
        createdAt: serverTimestamp(),
        approvedAt: null,
        approvedByUid: null,
      });

      toast.success(
        "Registration submitted. PMC approval ke baad aapka login activate hoga."
      );
      setTab("login");
    } catch (e) {
      console.error(e);
      if (e?.code === "auth/email-already-in-use") {
        toast.error("Ye email already registered hai. Please login.");
      } else {
        toast.error(e?.message || "Registration failed");
      }
    } finally {
      setRegLoading(false);
    }
  };

  /* ---------- LOGIN HANDLER ---------- */
  const handleLogin = async (e) => {
    e.preventDefault();
    if (!logEmail || !logPassword) {
      toast.error("Enter email & password");
      return;
    }
    try {
      setLogLoading(true);
      const email = logEmail.trim().toLowerCase();
      const { user } = await signInWithEmailAndPassword(
        auth,
        email,
        logPassword
      );

      const ref = doc(db, "portal_users", user.uid);
      const snap = await getDoc(ref);
      if (!snap.exists()) {
        toast.error("Registration not found. Please register.");
        await signOut(auth);
        return;
      }
      const data = snap.data();
      if (data.status !== "approved") {
        toast.error(
          `Your profile is ${data.status}. PMC approval ke baad hi access milega.`
        );
        await signOut(auth);
        return;
      }

      setProfile(data);
      toast.success("Login successful");
      // ✅ Ab user Profile Summary + My Dashboards card dekh sakta hai.
      // `/agency/dashboard` pe jane ke liye niche button diya gaya hai.
    } catch (e) {
      console.error(e);
      toast.error(e?.message || "Login failed");
    } finally {
      setLogLoading(false);
    }
  };

  const doLogout = async () => {
    await signOut(auth);
    setProfile(null);
    toast("Logged out");
  };

  const headerTitle =
    officeType === "BANK"
      ? "Bank / Financing Agency Portal"
      : officeType === "DISTRICT_REG"
      ? "District Registrar Office Portal"
      : officeType === "STATE_REG"
      ? "State Registrar Office Portal"
      : "Central Agency Portal";

  return (
    <>
      <Toaster position="top-center" />
      <div
        className={cx(
          "min-h-screen",
          dark ? "bg-slate-900 text-white" : "bg-emerald-50"
        )}
      >
        <div className="max-w-5xl mx-auto px-4 py-8">
          {/* Header */}
          <div className="rounded-2xl bg-white shadow-lg border border-emerald-200 px-6 py-5 mb-6 flex items-center justify-between">
            <div>
              <h1 className="text-2xl md:text-3xl font-extrabold text-emerald-800 tracking-tight">
                Stakeholder Portal
              </h1>
              <p className="text-sm text-gray-600">
                {headerTitle} – Gujarat godown &amp; storage projects ke liye
                secure access.
              </p>
            </div>
            <div className="flex items-center gap-2">
              {profile && (
                <button
                  onClick={doLogout}
                  className="text-xs px-3 py-2 rounded-lg bg-gray-900 text-white shadow hover:bg-black"
                >
                  Logout ({profile.officeType})
                </button>
              )}
              <button
                onClick={() => setDark((d) => !d)}
                className="text-xs px-3 py-2 rounded-lg border hover:bg-emerald-50"
              >
                {dark ? "Light" : "Dark"}
              </button>
            </div>
          </div>

          {/* Already logged-in → show summary + dashboard card */}
          {profile && (
            <>
              {/* Profile summary card */}
              <Card title="Profile Summary">
                <div className="text-sm">
                  <div className="font-semibold text-emerald-800">
                    {profile.officeName}
                  </div>
                  <div className="text-[11px] text-gray-600">
                    {profile.fullName} – {profile.email}
                  </div>
                  <div className="mt-1 text-[11px]">
                    <b>Type:</b> {profile.officeType} &nbsp;|&nbsp;{" "}
                    <b>State:</b> {profile.state}
                  </div>
                  <div className="mt-1 text-[11px]">
                    <b>Assigned Districts:</b>{" "}
                    {profile.assignedDistricts?.length
                      ? profile.assignedDistricts.join(", ")
                      : "PMC approval pending"}
                  </div>
                </div>
                <div className="text-[11px] text-gray-500 mt-3">
                  Detailed project / application dashboards hum stakeholder
                  dashboard se dikhate hain. Neeche “Open Dashboard” button se
                  aap apna live data dekh sakte hain.
                </div>
              </Card>

              {/* 🌟 My Dashboards – yahi wo area hai jahan tumne circle banaya tha */}
              <div className="mt-4">
                <Card title="My Dashboards">
                  <div className="text-sm text-slate-700">
                    <p>
                      Niche di gayi buttons se aap different dashboards open
                      kar sakte hain. Filhaal ek main dashboard available hai:
                    </p>
                    <ul className="mt-2 list-disc ml-5 text-[12px] space-y-1">
                      <li>
                        <b>Stakeholder Dashboard</b> – aapke assigned
                        districts ke sabhi projects &amp; applications ka live
                        view (Bank / Registrar / Agency ke role ke hisaab se).
                      </li>
                    </ul>

                    <div className="mt-4 flex flex-wrap gap-3">
                      <Link
                        to="/agency/dashboard"
                        className="px-5 py-2.5 rounded-xl bg-emerald-600 text-white text-sm font-semibold shadow hover:bg-emerald-700"
                      >
                        Open Stakeholder Dashboard
                      </Link>

                      {/* Future: yahan aur buttons add kar sakte ho,
                          jaise Bank MIS Report, DR NOC Dashboard, etc. */}
                    </div>

                    <p className="mt-3 text-[11px] text-slate-500">
                      Future me hum yahi se Bank-specific, District Registrar
                      specific aur Central Agency ke liye alag-alag advanced
                      dashboards add kar sakte hain, bina layout change kiye.
                    </p>
                  </div>
                </Card>
              </div>
            </>
          )}

          {/* Not logged-in or not approved → show Login/Register */}
          {!profile && (
            <>
              {/* Tabs */}
              <div className="flex justify-center gap-2 mb-6">
                {["login", "register"].map((t) => (
                  <button
                    key={t}
                    onClick={() => setTab(t)}
                    className={`px-5 py-2 rounded-xl text-sm font-semibold border ${
                      tab === t
                        ? "bg-emerald-600 text-white border-emerald-700"
                        : "bg-white text-emerald-700 border-emerald-200"
                    }`}
                  >
                    {t === "login"
                      ? "Login"
                      : "New Registration (Bank / Registrar / Agency)"}
                  </button>
                ))}
              </div>

              <div className="rounded-2xl bg-white shadow-xl border border-gray-200 p-6">
                {/* LOGIN */}
                {tab === "login" && (
                  <form
                    onSubmit={handleLogin}
                    className="max-w-md mx-auto grid gap-4"
                  >
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={logEmail}
                      onChange={setLogEmail}
                    />
                    <Label>Password</Label>
                    <Input
                      type="password"
                      value={logPassword}
                      onChange={setLogPassword}
                    />
                    <button
                      disabled={logLoading}
                      className="mt-2 py-3 rounded-xl bg-emerald-600 hover:bg-emerald-700 text-white font-bold"
                    >
                      {logLoading ? "Logging in…" : "Login"}
                    </button>
                  </form>
                )}

                {/* REGISTER */}
                {tab === "register" && (
                  <form onSubmit={handleRegister} className="grid gap-4">
                    <Card title="Basic Details">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Contact Person Name</Label>
                          <Input
                            value={fullName}
                            onChange={setFullName}
                          />
                        </div>
                        <div>
                          <Label>Office Name</Label>
                          <Input
                            value={officeName}
                            onChange={setOfficeName}
                            placeholder="e.g. SBI RO Banaskantha"
                          />
                        </div>
                      </div>
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Email (Login ID)</Label>
                          <Input
                            type="email"
                            value={regEmail}
                            onChange={setRegEmail}
                          />
                        </div>
                        <div>
                          <Label>Mobile</Label>
                          <Input
                            value={regPhone}
                            onChange={(v) =>
                              setRegPhone(v.replace(/\D/g, "").slice(0, 10))
                            }
                          />
                        </div>
                      </div>
                    </Card>

                    <Card title="Office Type & Location">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Office Type</Label>
                          <select
                            value={officeType}
                            onChange={(e) => setOfficeType(e.target.value)}
                            className="mt-1 w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-emerald-500"
                          >
                            {OFFICE_TYPES.map((o) => (
                              <option key={o.id} value={o.id}>
                                {o.label}
                              </option>
                            ))}
                          </select>
                        </div>
                        <div>
                          <Label>State</Label>
                          <select
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            className="mt-1 w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-emerald-500"
                          >
                            <option value="Gujarat">Gujarat</option>
                            {/* future me yaha UP etc. add kar sakte ho */}
                          </select>
                        </div>
                      </div>

                      {officeType === "CENTRAL_AGENCY" && (
                        <div className="mt-2">
                          <Label>Select Central Agency</Label>
                          <select
                            value={centralAgencyId}
                            onChange={(e) =>
                              setCentralAgencyId(e.target.value)
                            }
                            className="mt-1 w-full px-4 py-2.5 border rounded-xl focus:ring-2 focus:ring-emerald-500"
                          >
                            <option value="">Select Agency</option>
                            {CENTRAL_AGENCIES.map((a) => (
                              <option key={a.id} value={a.id}>
                                {a.label}
                              </option>
                            ))}
                          </select>
                          <p className="mt-1 text-[11px] text-gray-500">
                            Central agency ke liye sirf NAFED / Gujcomasol /
                            NCCF / NBCC / NABCONS hi allowed hain.
                          </p>
                        </div>
                      )}

                      {officeType !== "STATE_REG" &&
                        officeType !== "CENTRAL_AGENCY" && (
                          <div className="mt-2">
                            <Label>
                              Select District(s) in which you operate
                            </Label>
                            <div className="mt-2 grid md:grid-cols-3 gap-2 max-h-40 overflow-auto rounded-xl border border-emerald-100 p-2 bg-emerald-50/40">
                              {GUJ_DISTRICTS.map((d) => (
                                <label
                                  key={d}
                                  className="flex items-center gap-2 text-[12px]"
                                >
                                  <Checkbox
                                    checked={requestedDistricts.includes(d)}
                                    onChange={() => toggleDistrict(d)}
                                  />
                                  <span>{d}</span>
                                </label>
                              ))}
                            </div>
                            <p className="mt-1 text-[11px] text-gray-500">
                              PMC approval ke time ye districts verify/adjust
                              honge. Aapko final assigned districts approval
                              mail se milenge.
                            </p>
                          </div>
                        )}

                      {officeType === "STATE_REG" && (
                        <div className="mt-2 text-[11px] text-gray-600">
                          State Registrar ko poore Gujarat ka access diya
                          jayega. District list PMC admin panel se set hogi.
                        </div>
                      )}

                      {officeType === "CENTRAL_AGENCY" && (
                        <div className="mt-2 text-[11px] text-gray-600">
                          Central agencies ko multi-state/state-level view
                          milega. District mapping PMC master dashboard se
                          control hogi.
                        </div>
                      )}
                    </Card>

                    <Card title="Password">
                      <div className="grid md:grid-cols-2 gap-4">
                        <div>
                          <Label>Set Password</Label>
                          <Input
                            type="password"
                            value={regPassword}
                            onChange={setRegPassword}
                          />
                        </div>
                      </div>
                    </Card>

                    <div className="flex justify-end mt-4">
                      <button
                        disabled={regLoading}
                        className="px-6 py-2.5 rounded-xl bg-emerald-600 text-white font-semibold"
                      >
                        {regLoading ? "Submitting…" : "Submit Registration"}
                      </button>
                    </div>
                  </form>
                )}
              </div>
            </>
          )}

          <div className="text-center mt-8 text-sm">
            <Link to="/" className="text-emerald-700 underline">
              ← Back to Home
            </Link>
          </div>
        </div>
      </div>
    </>
  );
}
